import React, { useEffect, useMemo, useState } from "react";
import { getApiEndpoint } from "../../../utils/constants/api";
import { wfetch } from "../../../utils/fetch";
import { LINK_TARGETS } from "../../../utils/constants/linkTargets";
import { constructLinkNode, parseLinkNode } from "../../../utils/links";
import { parseRef } from "../../../utils/refs";
import { prepareRichText } from "../../../utils/richText";

// Marketing Form for the storefront, on the Smart Connector Marketing module.
//
// The dialog stores a form ref ({"source":"...","formId":"...","fields":[...]}) which the
// pages delivery endpoint resolves inline into the full Foundation form definition
// (multiSourceFormReferenceResolver) — so `form` usually arrives as an object and renders
// without an extra request. When it arrives unresolved (a raw JSON string, e.g. an endpoint
// without the resolver decoration), the definition is fetched client-side from
// GET /.rest/marketing/{source}/forms/{id} and the editor's field subset applied here.
//
// Submission POSTs the field values to /.rest/marketing/{source}/forms/{id}/leads — a real
// lead in the marketing backend (Marketo, Salesforce, ...), no demo fake.
//
// Submit options from the dialog: an optional disclaimer below the form, and after a
// successful submit a redirect to the thank-you page (resolved by the pages endpoint) or the
// inline success message. reCAPTCHA v3 runs only when /.rest/marketing/captcha reports a site
// key; the token goes in the X-Captcha-Token header.

// Shared across all Marketing Forms on the page: resolves to the site key once the reCAPTCHA
// script is ready, or null when captcha is not configured.
let captchaReady = null;
const loadCaptcha = () => {
    if (!captchaReady) {
        captchaReady = wfetch(getApiEndpoint("MARKETING_CAPTCHA")).then((res) => {
            const siteKey = res && res.data && res.data.siteKey;
            if (!siteKey) return null;
            // Google allows hiding the floating badge when the form shows the reCAPTCHA notice
            // instead (rendered below the form); the badge collides with chat buttons.
            const hide = document.createElement("style");
            hide.textContent = ".grecaptcha-badge { visibility: hidden; }";
            document.head.appendChild(hide);
            return new Promise((resolve) => {
                const script = document.createElement("script");
                script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
                script.onload = () => window.grecaptcha.ready(() => resolve(siteKey));
                script.onerror = () => resolve(null);
                document.head.appendChild(script);
            });
        });
    }
    return captchaReady;
};

const getCaptchaToken = async () => {
    const siteKey = await loadCaptcha();
    return siteKey ? window.grecaptcha.execute(siteKey, { action: "submit" }) : null;
};

const parseFormRef = (raw) => {
    const parsed = parseRef(raw);
    return parsed && parsed.formId ? parsed : null;
};

const applyFieldSubset = (definition, subset) => {
    if (!definition || !Array.isArray(subset) || !Array.isArray(definition.fields)) return definition;
    return {
        ...definition,
        fields: definition.fields.filter((f) => f.required || subset.includes(f.name)),
    };
};

// Stricter than the browser's built-in email check (which accepts "a@b"): require a TLD.
const EMAIL_PATTERN = "[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}";

// Conditional visibility (e.g. Marketo visibilityRules): action show|hide when ANY rule
// matches the visitor's current input. Unknown operators fail open (field stays visible)
// so a new backend operator never hides a field silently.
const KNOWN_OPERATORS = ["is", "isNot", "contains", "notContains", "startsWith"];

const matchesRule = (rule, subjectValue) => {
    const value = subjectValue == null ? "" : String(subjectValue);
    const candidates = (rule.values || []).map(String);
    switch (rule.operator) {
        case "is":
            return candidates.includes(value);
        case "isNot":
            return value !== "" && !candidates.includes(value);
        case "contains":
            return candidates.some((c) => value.includes(c));
        case "notContains":
            return value !== "" && !candidates.some((c) => value.includes(c));
        case "startsWith":
            return candidates.some((c) => value.startsWith(c));
        default:
            return false;
    }
};

const isFieldVisible = (field, values) => {
    const visibility = field.visibility;
    if (!visibility || !Array.isArray(visibility.rules) || visibility.rules.length === 0) return true;
    if (visibility.rules.some((r) => !KNOWN_OPERATORS.includes(r.operator))) return true; // fail open
    const matched = visibility.rules.some((r) => matchesRule(r, values[r.field]));
    return visibility.action === "hide" ? !matched : matched;
};

// Group fields into layout rows (e.g. Marketo rowNumber/columnNumber). Fields without
// layout info each get their own row — single-column rendering stays untouched.
const buildRows = (fields) => {
    const rows = new Map();
    fields.forEach((f, i) => {
        const key = f.row != null ? `r${f.row}` : `s${i}`;
        if (!rows.has(key)) rows.set(key, []);
        rows.get(key).push(f);
    });
    return Array.from(rows.values()).map((row) => row.sort((a, b) => (a.column || 0) - (b.column || 0)));
};

const FormField = ({ field, value, setValue }) => {
    const shared = {
        id: `mf-${field.name}`,
        required: !!field.required,
        className: "form-control",
        placeholder: field.placeholder || undefined,
        maxLength: field.maxLength || undefined,
        value: value ?? field.defaultValue ?? "",
        onChange: (e) => {
            e.target.setCustomValidity("");
            setValue(e.target.value);
        },
        // Backend-authored error text (e.g. Marketo's per-field validationMessage)
        // replaces the browser's generic message in the native validation bubble.
        onInvalid: field.validationMessage
            ? (e) => e.target.setCustomValidity(field.validationMessage)
            : undefined,
    };

    switch (field.type) {
        case "hidden":
            return <input type="hidden" name={field.name} value={value ?? field.defaultValue ?? ""} />;
        case "textarea":
            return <textarea rows={4} {...shared} />;
        case "select":
            return (
                <select {...shared}>
                    <option value="">{field.placeholder || "Please choose…"}</option>
                    {(field.options || []).map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label || o.value}
                        </option>
                    ))}
                </select>
            );
        case "checkbox":
            return (
                <input
                    id={shared.id}
                    type="checkbox"
                    className="form-check-input"
                    required={!!field.required}
                    checked={value === true}
                    onChange={(e) => setValue(e.target.checked)}
                />
            );
        case "radio":
            // One choice from options (e.g. Marketo radioButtons).
            return (
                <div>
                    {(field.options || []).map((o) => (
                        <div className="form-check" key={o.value}>
                            <input
                                id={`mf-${field.name}-${o.value}`}
                                type="radio"
                                name={field.name}
                                className="form-check-input"
                                required={!!field.required}
                                checked={(value ?? field.defaultValue) === o.value}
                                onChange={() => setValue(o.value)}
                            />
                            <label className="form-check-label" htmlFor={`mf-${field.name}-${o.value}`}>
                                {o.label || o.value}
                            </label>
                        </div>
                    ))}
                </div>
            );
        case "checkboxes": {
            // Multiple choices from options (e.g. Marketo checkboxes); value is an array,
            // joined to "; " on submit — the backend stores a single text value.
            const selected = Array.isArray(value) ? value : [];
            const toggle = (v) => setValue(selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v]);
            return (
                <div>
                    {(field.options || []).map((o) => (
                        <div className="form-check" key={o.value}>
                            <input
                                id={`mf-${field.name}-${o.value}`}
                                type="checkbox"
                                className="form-check-input"
                                checked={selected.includes(o.value)}
                                onChange={() => toggle(o.value)}
                            />
                            <label className="form-check-label" htmlFor={`mf-${field.name}-${o.value}`}>
                                {o.label || o.value}
                            </label>
                        </div>
                    ))}
                </div>
            );
        }
        default:
            // text, email, tel, number, url, date map 1:1 onto the input type.
            return <input type={field.type || "text"} pattern={field.type === "email" ? EMAIL_PATTERN : undefined} {...shared} />;
    }
};

const MarketingForm = ({ title, form, disclaimer, successMessage, thankYouPage }) => {
    const [status, setStatus] = useState("idle");
    const [errorMessage, setErrorMessage] = useState(null);
    const [fetched, setFetched] = useState(null);
    const [values, setValues] = useState({});

    // Resolved object straight from delivery, or a raw ref we resolve ourselves.
    const resolved = typeof form === "object" && form !== null ? form : null;
    const ref = resolved ? null : parseFormRef(form);

    useEffect(() => {
        if (!ref) return undefined;
        let cancelled = false;
        const endpoint = getApiEndpoint("MARKETING_FORM", {
            pathParams: { source: encodeURIComponent(ref.source), formId: encodeURIComponent(ref.formId) },
        });
        wfetch(endpoint).then((res) => {
            if (!cancelled) setFetched(res && res.data && res.data["@id"] ? applyFieldSubset(res.data, ref.fields) : null);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeof form === "string" ? form : null]);

    const [captchaActive, setCaptchaActive] = useState(false);

    useEffect(() => {
        let cancelled = false;
        // Load the captcha script early so the submit doesn't wait for it.
        loadCaptcha().then((siteKey) => {
            if (!cancelled) setCaptchaActive(!!siteKey);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const definition = resolved || fetched;
    const fields = useMemo(() => (definition && Array.isArray(definition.fields) ? definition.fields : []), [definition]);
    const rows = useMemo(() => buildRows(fields), [fields]);

    if (!definition || !definition.source) return null;

    const setValue = (name) => (v) => setValues((current) => ({ ...current, [name]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus("submitting");
        setErrorMessage(null);

        const payload = {};
        fields.forEach((f) => {
            if (!isFieldVisible(f, values)) return; // a rule-hidden field never submits
            let v = values[f.name] ?? f.defaultValue;
            if (Array.isArray(v)) v = v.join("; ");
            if (v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)) payload[f.name] = v;
        });

        const endpoint = getApiEndpoint("MARKETING_LEADS", {
            pathParams: {
                source: encodeURIComponent(definition.source),
                formId: encodeURIComponent(definition["@id"]),
            },
        });
        const headers = { "Content-Type": "application/json" };
        const captchaToken = await getCaptchaToken();
        if (captchaToken) headers["X-Captcha-Token"] = captchaToken;

        const res = await wfetch(endpoint, {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
        });

        if (res && res.data && !res.data.error && res.data.status !== "error") {
            const thankYouLink =
                thankYouPage && typeof thankYouPage === "object"
                    ? parseLinkNode(constructLinkNode({ field: LINK_TARGETS.WEBSITE, value: thankYouPage })).value
                    : null;
            if (thankYouLink) {
                window.location.assign(thankYouLink);
                return;
            }
            setStatus("success");
        } else {
            setStatus("error");
            setErrorMessage((res && res.data && (res.data.message || res.data.error)) || "Submission failed. Please try again.");
        }
    };

    return (
        <div className="marketing-form">
            {/* The backend's form description is authoring metadata (e.g. "Test form for
                the Addon dev team") — editors see it in the picker, visitors never should. */}
            <h2 className="mb-3">{title || definition.displayName || definition["@name"]}</h2>

            {status === "success" ? (
                successMessage ? (
                    <div className="marketing-form__success" dangerouslySetInnerHTML={{ __html: prepareRichText(successMessage) }} />
                ) : (
                    <div style={{ color: "#2ecc71" }}>Thank you! Your submission has been received.</div>
                )
            ) : (
                <form onSubmit={handleSubmit}>
                    {rows.map((row, rowIndex) => {
                        const visibleInRow = row.filter((field) => isFieldVisible(field, values));
                        if (visibleInRow.length === 0) return null;
                        return (
                            <div className="row" key={`row-${rowIndex}`}>
                                {visibleInRow.map((field) =>
                                    field.type === "hidden" ? (
                                        <FormField key={field.name} field={field} value={values[field.name]} setValue={setValue(field.name)} />
                                    ) : (
                                        <div className={"col-md " + (field.type === "checkbox" ? "mb-4 form-check" : "mb-4")} key={field.name}>
                                            <label
                                                className={field.type === "checkbox" ? "form-check-label" : "form-label fw-semibold"}
                                                htmlFor={`mf-${field.name}`}
                                            >
                                                {field.label || field.name}
                                                {field.required && <span style={{ color: "#e74c3c" }}> *</span>}
                                            </label>
                                            <FormField field={field} value={values[field.name]} setValue={setValue(field.name)} />
                                        </div>
                                    )
                                )}
                            </div>
                        );
                    })}

                    {disclaimer && (
                        <div
                            className="marketing-form__disclaimer mb-3"
                            style={{ fontSize: "0.8rem", color: "#6c757d" }}
                            dangerouslySetInnerHTML={{ __html: prepareRichText(disclaimer) }}
                        />
                    )}

                    {captchaActive && (
                        <div className="marketing-form__captcha-notice mb-3" style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                            This site is protected by reCAPTCHA and the Google{" "}
                            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                                Privacy Policy
                            </a>{" "}
                            and{" "}
                            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                                Terms of Service
                            </a>{" "}
                            apply.
                        </div>
                    )}

                    {status === "error" && <div className="mb-3" style={{ color: "#e74c3c" }}>{errorMessage}</div>}

                    <div className="btn-hover">
                        <button type="submit" className="default-btn" disabled={status === "submitting"}>
                            {status === "submitting" ? "Submitting…" : definition.submitLabel || "Submit"}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default MarketingForm;
